import { Card, CardContent } from '@/shared/ui/card';
import { ChevronDown, Loader2 } from 'lucide-react';
import { Button } from '@/shared/ui/button';
import { formatDateToLocaleString } from '@/shared/utils/date-utils';
import { useEffect, useMemo, useState } from 'react';
import { DropdownMenu, DropdownMenuCheckboxItem, DropdownMenuContent, DropdownMenuTrigger } from '@/shared/ui/dropdown-menu';
import { useSendAccountUsagesReport } from '@/domains/accounts/services/account-usages';
import { ERROR, SUCCESS } from '@/config/toast-message-config';
import { showErrorWithApiError, showSuccess } from '@/shared/utils/toast-utils';
import { DataConsumptionEvolutionChart } from '@/domains/global-dashboard/sections/consumption/DataConsumptionEvolutionChart';
import { OrganizationOverviewData } from '@/shared/types/overviewTypes';

export type DatapoolConsumptionChartContentProps = {
    selectedOrganizations: OrganizationOverviewData[];
};

/** Consumption bar chart and optional usage-report controls (no dialog shell). */
export function DatapoolConsumptionChartContent({
    selectedOrganizations,
}: DatapoolConsumptionChartContentProps) {

    const billingPeriods: { billingPeriod: string; label: string }[] = useMemo(() => {
        const months = selectedOrganizations.flatMap((organization) => organization.summary?.map((summary) => summary.startDate) ?? []);
        const uniqueMonths = [...new Set(months)];
        const sortedMonths = uniqueMonths.sort((a, b) => new Date(a).getTime() - new Date(b).getTime());
        return sortedMonths.reverse().slice(1, 6).map((month) => ({
            billingPeriod: month,
            label: formatDateToLocaleString(month, { year: 'numeric', month: 'long' }),
        }));
    }, [selectedOrganizations]);

    const [selectedBillingPeriods, setSelectedBillingPeriods] = useState<string[]>(
        billingPeriods.map((billingPeriod) => billingPeriod.billingPeriod),
    );
    const sendAccountUsageEmail = useSendAccountUsagesReport();
    const allMonthsSelected = billingPeriods.length > 0 && selectedBillingPeriods.length === billingPeriods.length;

    useEffect(() => {
        setSelectedBillingPeriods((prevSelectedBillingPeriods) => {
            const availableBillingPeriods = new Set(billingPeriods.map((billingPeriod) => billingPeriod.billingPeriod));
            const nextSelectedBillingPeriods = prevSelectedBillingPeriods.filter((billingPeriod) =>
                availableBillingPeriods.has(billingPeriod),
            );
            return nextSelectedBillingPeriods.length > 0
                ? nextSelectedBillingPeriods
                : billingPeriods.map((billingPeriod) => billingPeriod.billingPeriod);
        });
    }, [billingPeriods]);

    const handleToggleAllBillingPeriods = () => {
        setSelectedBillingPeriods((prevSelectedBillingPeriods) =>
            prevSelectedBillingPeriods.length === billingPeriods.length
                ? []
                : billingPeriods.map((billingPeriod) => billingPeriod.billingPeriod),
        );
    };

    const handleToggleBillingPeriod = (billingPeriod: string, checked: boolean | 'indeterminate') => {
        const isChecked = checked === true;
        setSelectedBillingPeriods((prevSelectedBillingPeriods) => {
            if (isChecked) {
                return prevSelectedBillingPeriods.includes(billingPeriod)
                    ? prevSelectedBillingPeriods
                    : [...prevSelectedBillingPeriods, billingPeriod];
            }
            return prevSelectedBillingPeriods.filter((selectedBillingPeriod) => selectedBillingPeriod !== billingPeriod);
        });
    };

    const handleSendAccountUsageEmail = async () => {
        if (!selectedOrganizations?.length) {
            return;
        }
        try {
            const months =
                selectedBillingPeriods.length > 0
                    ? selectedBillingPeriods
                    : billingPeriods.map((billingPeriod) => billingPeriod.billingPeriod);
            const monthsString = months
                .sort((a, b) => a.localeCompare(b))
                .reverse()
                .map((month) => formatDateToLocaleString(month, { year: 'numeric', month: 'short' }).split(' ').join('-'))
                .join(',');
            await sendAccountUsageEmail.mutateAsync({ months: monthsString });
            showSuccess(SUCCESS.ACCOUNT.SENT_USAGE_EMAIL);
        } catch (error) {
            showErrorWithApiError(error, ERROR.ACCOUNT.SENT_USAGE_EMAIL);
        }
    };

    return (
        <Card>
            <CardContent className="p-4 space-y-4">
                {selectedOrganizations?.length ? (
                    <>
                        <div className="flex items-center justify-between w-full gap-4">
                            <h2 className="text-md font-medium">Select month(s) to receive usage report(s) by email.</h2>
                            <div className="flex items-center gap-2 shrink-0">
                                <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                        <Button variant="outline" className="w-48 justify-between">
                                            {allMonthsSelected
                                                ? 'All Months'
                                                : selectedBillingPeriods.length > 0
                                                    ? `${selectedBillingPeriods.length} month${selectedBillingPeriods.length === 1 ? '' : 's'} selected`
                                                    : 'Select months'}
                                            <ChevronDown className="h-4 w-4" />
                                        </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end" className="w-56">
                                        <DropdownMenuCheckboxItem
                                            checked={allMonthsSelected}
                                            onSelect={(event) => event.preventDefault()}
                                            onCheckedChange={handleToggleAllBillingPeriods}
                                        >
                                            All Months
                                        </DropdownMenuCheckboxItem>
                                        {billingPeriods.map((billingPeriod) => (
                                            <DropdownMenuCheckboxItem
                                                key={billingPeriod.billingPeriod}
                                                checked={selectedBillingPeriods.includes(billingPeriod.billingPeriod)}
                                                onSelect={(event) => event.preventDefault()}
                                                onCheckedChange={(checked) =>
                                                    handleToggleBillingPeriod(billingPeriod.billingPeriod, checked)
                                                }
                                            >
                                                {billingPeriod.label}
                                            </DropdownMenuCheckboxItem>
                                        ))}
                                    </DropdownMenuContent>
                                </DropdownMenu>
                                <Button className="gap-2" onClick={handleSendAccountUsageEmail} disabled={selectedBillingPeriods.length === 0}>
                                    {sendAccountUsageEmail.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                                    {sendAccountUsageEmail.isPending ? 'Submitting...' : 'Get Report(s)'}
                                </Button>
                            </div>
                        </div>

                        <DataConsumptionEvolutionChart selectedOrganizations={selectedOrganizations} />
                    </>
                ) : (
                    <div className="text-center">No consumption data available to display.</div>
                )}
            </CardContent>
        </Card>
    );
}
